import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import EventLayout from "@/components/event/EventLayout";
import AddToHomeScreen from "@/components/event/AddToHomeScreen";
import { eventApi, humanDay } from "@/lib/eventHub";
import { Button } from "@/components/ui/button";
import {
  CalendarDays, ScanLine, Heart, ArrowRight, Rocket, MapPin,
  Camera, Sparkles, Zap, TrendingUp, BookOpen,
} from "lucide-react";

export default function EventHome() {
  const [config, setConfig] = useState(null);
  const [customAmount, setCustomAmount] = useState("");

  useEffect(() => {
    let m = true;
    (async () => { try { const c = await eventApi.config(); if (m) setConfig(c.data); } catch { /* noop */ } })();
    return () => { m = false; };
  }, []);

  const friday = config?.event?.friday;
  const saturday = config?.event?.saturday;
  const donation = config?.donation;

  const donateLink = useMemo(() => {
    if (!donation?.url) return "#";
    if (!customAmount) return donation.url;
    try {
      const u = new URL(donation.url);
      u.searchParams.set("amount", String(customAmount));
      return u.toString();
    } catch { return donation.url; }
  }, [donation?.url, customAmount]);

  return (
    <EventLayout showProgramBanner={false}>
      {/* ─── HERO ─────────────────────────────────────────────────────────── */}
      <section
        data-testid="event-hero"
        className="relative overflow-hidden bg-emerald-950 text-white"
      >
        {/* Layered brand gradients */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Fuchsia burst top-right */}
          <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-fuchsia-600 opacity-30 blur-3xl" />
          {/* Emerald burst bottom-left */}
          <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-emerald-500 opacity-25 blur-3xl" />
          {/* Diagonal ray — subtle emerald wash instead of a fuchsia one that read purple on some screens */}
          <div className="absolute top-0 right-0 h-full w-full bg-gradient-to-br from-transparent via-transparent to-emerald-500/8" />
          {/* Dot texture */}
          <svg
            className="absolute inset-0 h-full w-full opacity-[0.08]"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern id="dots" width="24" height="24" patternUnits="userSpaceOnUse">
                <circle cx="1.5" cy="1.5" r="1" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
        </div>

        {/* City skyline silhouette (SVG, brand-native) */}
        <svg
          className="absolute bottom-0 inset-x-0 w-full opacity-30"
          viewBox="0 0 400 90"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMax slice"
          aria-hidden="true"
        >
          <path
            d="M0 90 L0 60 L20 60 L20 45 L35 45 L35 55 L45 55 L45 30 L60 30 L60 40 L75 40 L75 20 L90 20 L90 35 L105 35 L105 50 L120 50 L120 25 L135 25 L135 15 L155 15 L155 40 L170 40 L170 55 L190 55 L190 30 L210 30 L210 45 L225 45 L225 20 L245 20 L245 35 L260 35 L260 50 L275 50 L275 25 L295 25 L295 40 L315 40 L315 55 L330 55 L330 30 L350 30 L350 45 L370 45 L370 60 L400 60 L400 90 Z"
            fill="#064e3b"
          />
        </svg>

        <div className="relative mx-auto max-w-lg px-5 pt-8 pb-16 text-center">
          {/* Eyebrow */}
          <div
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 px-3 py-1 mb-4"
          >
            <Rocket size={11} className="text-fuchsia-300" />
            <span className="text-[10px] tracking-[0.24em] uppercase font-[Outfit] font-bold text-fuchsia-100">
              2026 · Fast Forward
            </span>
          </div>

          {/* Main title */}
          <h1 className="font-[Cormorant_Garamond] leading-[0.98] text-white text-[42px] sm:text-6xl">
            Freedom In Me
            <br />
            <span className="inline-block text-fuchsia-500">
              Business Showers
            </span>
          </h1>

          {/* Fast Forward tag — solid fuchsia + emerald so it reads as pink + green, never purple */}
          <div className="mt-4 flex items-center justify-center gap-2">
            <span className="italic font-[Cormorant_Garamond] text-2xl sm:text-3xl">
              <span className="text-fuchsia-500">Fast</span>{" "}
              <span className="text-emerald-400">FORWARD</span>
            </span>
            <TrendingUp size={20} className="text-emerald-300" />
          </div>

          {/* Tagline */}
          <p className="mt-3 font-[Outfit] text-sm sm:text-base text-white/70 max-w-sm mx-auto">
            Bold Moves. Smart Systems. Stronger Futures.
          </p>

          {/* Meta row */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs font-[Outfit] text-white/80">
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays size={13} className="text-fuchsia-300" />
              <span className="font-semibold">July 17–18, 2026</span>
            </span>
            <span className="text-white/25">·</span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={13} className="text-emerald-300" />
              <span>Renaissance Hotel South Park · Charlotte, NC</span>
            </span>
          </div>

          {/* Primary CTAs */}
          <div className="mt-8 space-y-3">
            <Button
              asChild
              data-testid="cta-checkin"
              className="w-full h-16 rounded-2xl bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-[Outfit] font-bold text-lg tracking-wide shadow-[0_20px_40px_-15px_rgba(217,70,239,0.6)] active:scale-[0.98] transition-all"
            >
              <Link to="/event/checkin" onClick={() => eventApi.track("cta_checkin_click")}>
                <ScanLine className="mr-2" size={22} />
                Check In
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="cta-program"
              className="w-full h-14 rounded-2xl border-2 border-white/30 hover:border-white/60 hover:bg-white/10 text-white font-[Outfit] font-bold text-base tracking-wide bg-white/5 backdrop-blur"
            >
              <Link to="/event/program" onClick={() => eventApi.track("cta_program_click")}>
                <BookOpen className="mr-2" size={18} />
                View Program Booklets
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ─── EVENT DAY CARDS ─────────────────────────────────────────────── */}
      <section data-testid="event-day-cards" className="relative -mt-6 z-10 px-5 pb-10">
        <div className="mx-auto max-w-lg space-y-4">
          {/* PWA install CTA · shows only when installable + not yet installed */}
          <AddToHomeScreen variant="card" className="mb-1" />
          {friday && (
            <EventDayCard
              day={friday}
              accent="fuchsia"
              nightLabel="Night One"
              subtitle="VIP Networking Experience"
            />
          )}
          {saturday && (
            <EventDayCard
              day={saturday}
              accent="emerald"
              nightLabel="Main Event"
              subtitle="The Ceremony"
            />
          )}
        </div>
      </section>

      {/* ─── DONATION ─────────────────────────────────────────────────────── */}
      <section
        data-testid="event-donation"
        id="donation"
        className="mx-4 mb-12 rounded-[28px] overflow-hidden shadow-xl relative"
      >
        {/* Dark card with gradient border */}
        <div className="relative bg-emerald-950 p-6 pb-8 text-white">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 h-40 w-40 rounded-full bg-fuchsia-600 opacity-25 blur-3xl" />
          </div>
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <span className="h-9 w-9 rounded-full bg-fuchsia-500 text-white flex items-center justify-center shadow-lg shadow-fuchsia-500/40">
                <Heart size={18} fill="currentColor" />
              </span>
              <span className="text-[10px] tracking-[0.24em] uppercase font-[Outfit] font-bold text-fuchsia-300">
                Give Back
              </span>
            </div>
            <h3 className="font-[Cormorant_Garamond] text-3xl leading-tight mb-2">
              Help Us Support the Next Class of Small Businesses
            </h3>
            <p className="font-[Outfit] text-sm text-white/70 leading-relaxed mb-5">
              {donation?.impact || "Every gift underwrites coaching, resources, and the community that carries our honorees forward."}
            </p>

            {donation?.amounts?.length ? (
              <>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {donation.amounts.map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      data-testid={`donation-amount-${amt}`}
                      onClick={() => setCustomAmount(String(amt))}
                      className={`rounded-xl border-2 font-[Outfit] font-bold py-3 text-base transition-all ${
                        String(customAmount) === String(amt)
                          ? "border-fuchsia-400 bg-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/40"
                          : "border-white/15 bg-white/5 text-white hover:border-fuchsia-300"
                      }`}
                    >
                      ${amt}
                    </button>
                  ))}
                </div>
                <label className="block mb-4">
                  <span className="text-[10px] font-[Outfit] font-bold text-white/50 uppercase tracking-[0.16em]">Custom amount</span>
                  <div className="mt-1 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 font-[Outfit] font-bold text-white/50">$</span>
                    <input
                      type="number" inputMode="numeric" min="1"
                      data-testid="donation-custom-amount"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value.replace(/[^0-9]/g, ""))}
                      className="w-full h-12 rounded-xl border-2 border-white/15 bg-white/5 pl-8 pr-4 font-[Outfit] font-bold text-lg text-white placeholder:text-white/30 focus:border-fuchsia-400 outline-none"
                      placeholder="Other amount"
                    />
                  </div>
                </label>
              </>
            ) : null}

            {donation?.url ? (
              <Button
                asChild
                data-testid="donation-cta"
                className="w-full h-14 rounded-xl bg-white text-emerald-950 hover:bg-fuchsia-50 font-[Outfit] font-bold text-base tracking-wide shadow-lg"
              >
                <a
                  href={donateLink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => eventApi.track("donation_click", { amount: customAmount || "" })}
                >
                  <Zap size={18} className="mr-2 text-fuchsia-600" />
                  Donate Now
                  <ArrowRight className="ml-2" size={18} />
                </a>
              </Button>
            ) : (
              <div
                data-testid="donation-unavailable"
                className="rounded-xl border border-dashed border-white/20 bg-white/5 p-4 text-center text-xs font-[Outfit] text-white/60"
              >
                Donations open soon. Administrators can add the donation link from the Admin dashboard.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── QUICK LINKS ─────────────────────────────────────────────────── */}
      <section data-testid="event-quick-links" className="px-5 pb-4">
        <div className="mx-auto max-w-lg grid grid-cols-2 gap-3">
          <Link
            to="/event/photos"
            data-testid="quick-link-photos"
            className="group rounded-2xl border-2 border-emerald-950/10 bg-white p-4 hover:border-fuchsia-400 hover:shadow-lg transition-all"
          >
            <span className="inline-flex h-9 w-9 rounded-lg bg-fuchsia-100 text-fuchsia-700 items-center justify-center mb-2 group-hover:bg-fuchsia-600 group-hover:text-white transition-colors">
              <Camera size={16} />
            </span>
            <div className="text-[10px] tracking-[0.18em] uppercase font-[Outfit] font-bold text-fuchsia-700">Share</div>
            <div className="font-[Cormorant_Garamond] text-xl leading-tight text-emerald-950">Event Photos</div>
          </Link>
          <Link
            to="/event/more"
            data-testid="quick-link-momentum"
            className="group rounded-2xl border-2 border-emerald-950/10 bg-white p-4 hover:border-emerald-500 hover:shadow-lg transition-all"
          >
            <span className="inline-flex h-9 w-9 rounded-lg bg-emerald-100 text-emerald-700 items-center justify-center mb-2 group-hover:bg-emerald-700 group-hover:text-white transition-colors">
              <Sparkles size={16} />
            </span>
            <div className="text-[10px] tracking-[0.18em] uppercase font-[Outfit] font-bold text-emerald-700">Explore</div>
            <div className="font-[Cormorant_Garamond] text-xl leading-tight text-emerald-950">Keep the Momentum</div>
          </Link>
        </div>
      </section>
    </EventLayout>
  );
}

// ─── Reusable premium event day card — fully clickable ────────────────────
function EventDayCard({ day, accent, nightLabel, subtitle }) {
  const isDark = accent === "fuchsia";
  const isFriday = day.date === "2026-07-17";
  const slug = isFriday ? "friday" : "saturday";
  return (
    <Link
      to={`/event/day/${slug}`}
      data-testid={`event-card-${slug}`}
      aria-label={`Open ${day.name} — ${day.times}`}
      className={`group block relative overflow-hidden rounded-3xl p-5 sm:p-6 shadow-xl transition-all hover:-translate-y-1 hover:shadow-2xl active:scale-[0.99] focus:outline-none focus-visible:ring-4 focus-visible:ring-fuchsia-400/60 ${
        isDark ? "bg-fuchsia-600 text-white" : "bg-white border-2 border-emerald-700 text-emerald-950"
      }`}
    >
      {/* Decorative element */}
      <div className={`absolute -top-8 -right-8 h-32 w-32 rounded-full ${isDark ? "bg-white/10" : "bg-emerald-100"}`} />
      <div className={`absolute bottom-0 left-0 h-16 w-16 rounded-full ${isDark ? "bg-white/5" : "bg-fuchsia-100/60"} -translate-x-8 translate-y-8`} />

      <div className="relative">
        <div className="flex items-baseline justify-between mb-2">
          <span className={`text-[10px] tracking-[0.24em] uppercase font-[Outfit] font-bold ${isDark ? "text-white/90" : "text-fuchsia-700"}`}>
            {nightLabel}
          </span>
          <span className={`text-[10px] font-[Outfit] font-bold uppercase tracking-widest ${isDark ? "text-white/60" : "text-emerald-950/40"}`}>
            {isFriday ? "Day 1" : "Day 2"}
          </span>
        </div>
        <div className={`text-[10px] font-[Outfit] font-bold uppercase tracking-widest mb-1 ${isDark ? "text-fuchsia-100" : "text-emerald-700"}`}>
          {subtitle}
        </div>
        <h2 className="font-[Cormorant_Garamond] text-3xl leading-tight">
          {day.name}
        </h2>
        <div className={`mt-3 flex flex-col sm:flex-row sm:items-center sm:gap-3 gap-1 text-xs font-[Outfit] ${isDark ? "text-white/80" : "text-emerald-950/70"}`}>
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays size={13} />
            <span className="font-semibold">{humanDay(day.date)}</span>
          </span>
          <span className={isDark ? "text-white/40" : "text-emerald-950/30"}>·</span>
          <span className="font-semibold">{day.times}</span>
        </div>
        {/* Explicit CTA affordance */}
        <div className={`mt-4 inline-flex items-center gap-1.5 text-[11px] font-[Outfit] font-bold uppercase tracking-widest ${isDark ? "text-white" : "text-fuchsia-700"} group-hover:gap-2.5 transition-all`}>
          Open this event
          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}
